import { computed, ref, watch } from 'vue';
import { type RouteRecordRaw } from 'vue-router';
import { isNil } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useViewModeStore } from '@/common/stores/view-mode';
import { useInstanceCount } from '@/common/composables/useInstanceCount';
import { useGetV7KubernetesClustersOpenshiftExists } from '@/api-client/kubernetes/queries/kubernetes-cluster-v7-controller/kubernetes-cluster-v7-controller';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import {
  K8S_GNB_GROUPS,
  type K8sGnbItem,
} from '@/common/components/organisms/baseMenu/kubernetesGnbGroups.define';
import { VIEW_MODE, DB_TYPE } from '@/common/define/common.define';
import {
  getApplicationNaviMenu,
  getRumNaviMenu,
  getApplicationPerformanceAnalysisMenu,
} from '@/application/router/routes';
import { getInfrastructureNaviMenu } from '@/infrastructure/router/routes';
import { getGpuNaviMenu } from '@/gpu/router/routes';
import { getCloudNaviMenu } from '@/cloud/router/routers';
import { getKubernetesNaviMenu } from '@/kubernetes/router/routes';
import { getPgNaviMenu } from '@/postgresql/router/routes';
import { getOracleNaviMenu } from '@/oracle/router/routes';
import { getMysqlNaviMenu } from '@/mysql/router/routes';
import { getSqlServerNaviMenu } from '@/sqlServer/router/routes';
import { getAltibaseNaviMenu } from '@/database/router/altibase.routes';
import { getTiberoNaviMenu } from '@/database/router/tibero.routes';
import { getClickhouseNaviMenu } from '@/database/router/clickhouse.routes';
import { getRedshiftNaviMenu } from '@/database/router/redshift.routes';
import { getSingleStoreNaviMenu } from '@/database/router/singlestore.routes';
import { getReportNaviMenu } from '@/report/router/routes';
import { getConfigNaviMenu } from '@/config/router/routes';
import { getBusinessNaviMenu } from '@/service/router/router';
import { getLoggingNaviMenu, getStreamNaviMenu } from '@/logs/router/routes';
import { getToolMenu } from '@/tool/router/routes';
import { getDatabaseNaviMenu } from '@/database/router/routes';
import { getAlertNaviMenu } from '@/alert/router/routes';
import { getHelpNaviMenu } from '@/help/router/routes';
import { convertCamelToCapital, getLocaleKey } from '@/common/utils/commonUtils';
import { sortByString } from '@/common/utils/sort.utils';
import { i18n, type MessageSchema, useInternational } from '@/common/locale';
import {
  DashboardId,
  PlatformDashboardValue,
  usePlatformDashboardStore,
} from '@/common/stores/platform-dashboard';
import { findPermissionByTranslateId } from '@/common/permission/permission.utils';
import { DASHBOARD_MENU_TYPE } from '@/common/components/molecules/breadCrumbs/breadCrumbs.define';
import { invalidatePlatformDashboardMenuPath } from '@/common/lib/queryCache';
import { useDashboardListStore } from '@/dashboard/stores/dashboard-list';
import { postV6DashboardsFavorite } from '@/api-client/dashboard/queries/integrated-dashboard-controller/integrated-dashboard-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { getMessageQueueMenu } from '@/messageQueue/router/routes';
import { type MaxgaugeMenuDbType } from '@/common/utils';
import { getKubernetesAnalysisMenu } from '@/kubernetesAnalysis/router/routes';
import { hasDatabaseDashboardPermission } from '@/database/utils/permission.utils';
import { useMyInfoStore } from '@/config/stores/my-info';
import { useGetV11TargetByCategoryCount } from '@/api-client/target/queries/target-status-controller/target-status-controller';
import type { MenuInfo } from './types';
import {
  BASE_MENU_VALUE,
  MAXGAUGE_DATABASE_MENU_KEY_MAPPER,
  SUBTREE_ARRAY,
} from './baseMenu.define';
import { getPredefinedReportNaviMenu } from '@/report/predefinedReport/router/routes';

export const hasNoChildren = (menu: MenuInfo): boolean =>
  !menu.children || menu.children.length < 1;

export const useDashboardMenu = (menuInfo: MenuInfo[]) => {
  const dashboardMenuInfo = ref<Record<string, MenuInfo[]>>({
    [BASE_MENU_VALUE.APPLICATION]: [],
    [BASE_MENU_VALUE.KUBERNETES]: [],
    [BASE_MENU_VALUE.DATABASE]: [],
    [BASE_MENU_VALUE.INFRASTRUCTURE]: [],
    [BASE_MENU_VALUE.CLOUD]: [],
    [BASE_MENU_VALUE.BUSINESS]: [],
    [BASE_MENU_VALUE.LOGS]: [],
    [BASE_MENU_VALUE.ALERT]: [],
  });
  const analysisBoardMenuInfo = ref<Record<string, MenuInfo[]>>({
    [BASE_MENU_VALUE.APPLICATION]: [],
    [BASE_MENU_VALUE.KUBERNETES]: [],
    [BASE_MENU_VALUE.DATABASE]: [],
    [BASE_MENU_VALUE.INFRASTRUCTURE]: [],
    [BASE_MENU_VALUE.BUSINESS]: [],
    [BASE_MENU_VALUE.LOGS]: [],
    [BASE_MENU_VALUE.ALERT]: [],
  });

  const platformDashboardStore = usePlatformDashboardStore();
  const { menuByPlatform, menuByAnalysisPlatform } = storeToRefs(platformDashboardStore);

  const dashboardSortFn = (infoA: MenuInfo, infoB: MenuInfo) => {
    return sortByString(infoA.text, infoB.text);
  };

  watch(
    menuByPlatform,
    (platformList) => {
      menuInfo.forEach((menu) => {
        const menuValue = menu.value === BASE_MENU_VALUE.KUBERNETES ? 'k8s' : menu.value;
        const platform: Record<DashboardId, PlatformDashboardValue> = platformList![menuValue];
        if (!platform) return;

        const dashboards = Object.entries(platform);
        if (dashboards.length) {
          const menuInfoIdx = menuInfo.findIndex((info) => info.value === menu.value);
          if (menuInfoIdx === undefined) {
            return;
          }
          const children = dashboards.map(([dashboardId, contents]) => ({
            text: contents.name,
            value: `dashboard_Dashboard View_${dashboardId}`,
            path: `/dashboard/view/${dashboardId}?rootMenu=${menu.value}&menuType=${DASHBOARD_MENU_TYPE.DASHBOARD}`,
            favoriteId: contents.favoriteId,
            featuredIcon: contents.iconId,
          }));
          dashboardMenuInfo.value[menu.value] = children.sort(dashboardSortFn);
        } else {
          dashboardMenuInfo.value[menu.value] = [];
        }
      });
    },
    { deep: true, immediate: true },
  );
  watch(
    menuByAnalysisPlatform,
    (platformList) => {
      menuInfo.forEach((menu) => {
        const menuValue = menu.value === BASE_MENU_VALUE.KUBERNETES ? 'k8s' : menu.value;
        const platform: Record<DashboardId, PlatformDashboardValue> = platformList![menuValue];
        if (!platform) return;

        const dashboards = Object.entries(platform);
        if (dashboards.length) {
          const menuInfoIdx = menuInfo.findIndex((info) => info.value === menu.value);
          if (menuInfoIdx === undefined) {
            return;
          }
          const children = dashboards.map(([dashboardId, contents]) => ({
            text: contents.name,
            value: `dashboard_Dashboard View_${dashboardId}`,
            path: `/dashboard/view/${dashboardId}?rootMenu=${menu.value}&menuType=${DASHBOARD_MENU_TYPE.ANALYSIS_DASHBOARD}`,
            favoriteId: contents.favoriteId,
            featuredIcon: contents.iconId,
          }));
          analysisBoardMenuInfo.value[menu.value] = children.sort(dashboardSortFn);
        } else {
          analysisBoardMenuInfo.value[menu.value] = [];
        }
      });
    },
    { deep: true, immediate: true },
  );

  return { dashboardMenuInfo, analysisBoardMenuInfo };
};

export const useMenu = () => {
  const { t } = useInternational();
  // 쿠버네티스 LNB(탭) 메뉴는 영문 공통 적용(다국어 제외). en 로케일의 GNB 메시지에서 직접 조회한다.
  const enGnbMessages = i18n.global.getLocaleMessage<MessageSchema>('en').WORD.GNB;
  const translateEn = (gnbKey: string): string => {
    const value = enGnbMessages[gnbKey as keyof typeof enGnbMessages];
    return typeof value === 'string' ? value : t(`WORD.GNB.${gnbKey}`);
  };
  const { viewMode } = useViewModeStore();
  const { role: roleId } = useMyInfoStore().myInfo;

  // instance에 따라 maxgauge 메뉴 목록이 달라짐
  const { instanceCount } = useInstanceCount();

  // 커스텀 환경 상태 (OpenShift 클러스터 존재 여부)
  const { data: openShiftExists, isSuccess: isOpenShiftChecked } =
    useGetV7KubernetesClustersOpenshiftExists({
      query: {
        select: (response) => response.data?.data ?? false,
      },
    });

  const openShiftCluster = computed(() => ({
    exists: openShiftExists.value ?? false,
    isChecked: isOpenShiftChecked.value,
  }));

  // DFS 사용
  const findMenuItem = (
    menuTree: MenuInfo[] = [],
    predicate: (node: MenuInfo) => boolean,
    menuVisitRoute: MenuInfo[] = [],
  ): MenuInfo | null => {
    if (!menuTree.length) {
      return null;
    }

    for (let i = 0; i < menuTree.length; i++) {
      const menuNode = menuTree[i];
      if (predicate(menuNode)) {
        menuVisitRoute.unshift(menuNode);
        return menuNode;
      }

      for (let j = 0; j < SUBTREE_ARRAY.length; j++) {
        if (menuNode[SUBTREE_ARRAY[j]]) {
          const foundNode = findMenuItem(menuNode[SUBTREE_ARRAY[j]], predicate, menuVisitRoute);
          if (foundNode) {
            if (menuNode[SUBTREE_ARRAY[j]]?.find((child) => child.value === foundNode.value)) {
              foundNode.parentText = menuNode.text;
              foundNode.parentValue = menuNode.value;
            }
            menuVisitRoute.unshift(menuNode);
            return foundNode;
          }
        }
      }
    }

    return null;
  };

  const getMenuName = (routeName: string): string => {
    if (routeName === 'application_Overview') {
      // APM 오버뷰 메뉴명만 전용 라벨로 — 공유 키 WORD.GNB.OVERVIEW(MaxGauge DB 등과 공용) 를 건드리지 않기 위함.
      return t('WORD.GNB.SERVICE_STATUS_MONITORING');
    }
    if (routeName === '') {
      // NOTE: 고객지원 탭의 특정 메뉴는 카테고리명이 존재하지 않음
      return '';
    }
    const menuName = routeName.slice(routeName.indexOf('_') + 1);
    const localeKey = getLocaleKey(menuName);
    // NOTE: 쿠버 서비스는 서비스, 그 외 서비스는 비즈니스로 변경해야함
    const localeMessageKey = routeName === 'kubernetes_Service' ? 'KUBE_SERVICE' : localeKey;
    // 쿠버네티스 LNB(탭) 메뉴는 영문 공통 적용(다국어 제외). GNB 그룹/항목 라벨은 K8S_GNB_GROUPS define에서 지정한다.
    if (routeName.startsWith('kubernetes_')) {
      return translateEn(localeMessageKey);
    }
    return t(`WORD.GNB.${localeMessageKey}`);
  };

  const getChildrenList = (
    item: RouteRecordRaw,
    subItem: RouteRecordRaw,
    parentPath: string,
  ): MenuInfo[] =>
    (subItem.children ?? []).map((sub) => {
      const subPath =
        parentPath.substring(-1) === '/' ? `${parentPath}${sub.path}` : `${parentPath}/${sub.path}`;
      return {
        text: getMenuName(sub.name as string),
        value: `${item.path.split('/')[1]}_${sub.path}`,
        path: subPath,
        disabled: (sub.props as Record<string, any>)?.disabled,
        children: sub.children ? getChildrenList(item, sub, subPath) : [],
      };
    });

  // 메뉴 목록
  const getMenuList = (routerInfo: RouteRecordRaw[]): MenuInfo[] =>
    routerInfo
      .filter((item) => !item.meta?.invisibleViewMode?.includes(viewMode))
      .flatMap<MenuInfo>((item) => {
        const childList = item.children
          ?.filter((child) => !child.meta?.invisibleViewMode?.includes(viewMode))
          .flatMap<MenuInfo>((subItem) => {
            const subItemName = subItem.name as string;
            return {
              text: getMenuName(subItemName) || convertCamelToCapital(subItem.path),
              value: subItemName.split('_')[0]
                ? `${subItemName.split('_')[0]}_${subItem.path}`
                : subItem.path,
              path: `${item.path}${subItem.path}`,
              disabled: (subItem.props as Record<string, any>)?.disabled,
              children: getChildrenList(item, subItem, `${item.path}${subItem.path}`),
              meta: subItem.meta,
            };
          });

        const itemName = item.name as string;
        const path = childList ? '' : item.path;
        const disabled = childList ? undefined : (item.props as Record<string, any>)?.disabled;

        return {
          text: getMenuName(itemName),
          value: itemName,
          path,
          children: childList,
          disabled,
          meta: item.meta,
        };
      });

  const getNaviMenuForMaxgauge = {
    oracle: getOracleNaviMenu,
    postgresql: getPgNaviMenu,
    mysql: getMysqlNaviMenu,
    sqlserver: getSqlServerNaviMenu,
    altibase: getAltibaseNaviMenu,
    tibero: getTiberoNaviMenu,
    clickhouse: getClickhouseNaviMenu,
    redshift: getRedshiftNaviMenu,
    singlestore: getSingleStoreNaviMenu,
  };

  const BASE_MENU_MAP: Record<MaxgaugeMenuDbType, string> = {
    oracle: BASE_MENU_VALUE.ORACLE,
    sqlserver: BASE_MENU_VALUE.SQLSERVER,
    postgresql: BASE_MENU_VALUE.POSTGRESQL,
    mysql: BASE_MENU_VALUE.MYSQL,
    altibase: BASE_MENU_VALUE.ALTIBASE,
    tibero: BASE_MENU_VALUE.TIBERO,
    clickhouse: BASE_MENU_VALUE.CLICKHOUSE,
    redshift: BASE_MENU_VALUE.REDSHIFT,
    singlestore: BASE_MENU_VALUE.SINGLESTORE,
  };

  const createMaxgaugeMenuItem = (dbType: MaxgaugeMenuDbType) => {
    const menuList = getMenuList(getNaviMenuForMaxgauge[dbType]());
    const baseMenuValue = BASE_MENU_MAP[dbType] ?? '';

    return menuList
      .map((menu) => {
        const mappedKey = MAXGAUGE_DATABASE_MENU_KEY_MAPPER[dbType][menu.value];
        if (!mappedKey || !menu.children?.length) {
          return null;
        }
        return {
          text: t(mappedKey.menuTextKey),
          value: `${baseMenuValue}_${mappedKey.menuKey}`,
          children: menu.children,
        };
      })
      .filter((item) => !isNil(item));
  };

  // children: 최하위 leaf node (예시: Kubernetes > Pod, Infrastructure > Host)
  // subMenuList: 클릭하면 우측 메뉴가 펼쳐지는 하위 메뉴 (예시: Settings)

  // 홈 메뉴 목록
  const homeMenuInfo = {
    text: t('WORD.GNB.HOME'),
    value: BASE_MENU_VALUE.HOME,
    path: '/home',
    children: undefined,
  };

  // 대시보드 메뉴 목록 (1-Depth 클릭 시 대시보드 목록 화면으로 바로 이동)
  const dashboardMenuInfo = {
    text: t('WORD.GNB.DASHBOARD'),
    value: BASE_MENU_VALUE.DASHBOARD,
    path: '/dashboard/list',
    children: undefined,
  };

  // 하위 대시보드 메뉴 목록
  const subDashboardMenuInfo = {
    text: t('WORD.GNB.DASHBOARD'),
    value: BASE_MENU_VALUE.DASHBOARD,
    subMenuList: [],
  };

  // 하위 분석보드 메뉴 목록
  const subAnalysisBoardMenuInfo = {
    text: t('WORD.GNB.ANALYSISBOARD'),
    value: BASE_MENU_VALUE.ANALYSISBOARD,
    subMenuList: [],
  };

  // 인프라 > 대시보드 메뉴
  const infrastructureDashboardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subDashboardMenuInfo
      : undefined;

  const infrastructureAnalysisBoardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subAnalysisBoardMenuInfo
      : undefined;

  // 인프라 메뉴 목록 (도구 메뉴 제거 - 도구는 이제 1-Depth로 분리됨)
  const infrastructureMenuInfo: MenuInfo[] = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_INFRASTRUCTURE,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.INFRASTRUCTURE'),
          value: BASE_MENU_VALUE.INFRASTRUCTURE,
          children: getMenuList(getInfrastructureNaviMenu())[0]?.children?.filter(
            (child) => child.value !== `${BASE_MENU_VALUE.INFRASTRUCTURE}_Tool`,
          ),
          dashboardList: infrastructureDashboardList,
          analysisBoardList: infrastructureAnalysisBoardList,
        },
      ]
    : [];

  // GPU 메뉴 목록 (GPU 도메인 그룹 권한, 하위 메뉴는 각 라우트 권한으로 필터, exemONE 전용)
  const gpuMenuInfo: MenuInfo[] = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_GPU_MONITORING,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.GPU'),
          value: BASE_MENU_VALUE.GPU,
          children: getMenuList(getGpuNaviMenu())[0]?.children,
        },
      ]
    : [];

  // 메시지 큐 > 대시보드
  const messageQueueDashboardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_MESSAGEQUEUE_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subDashboardMenuInfo
      : undefined;

  const messageQueueAnalysisBoardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_MESSAGEQUEUE_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subAnalysisBoardMenuInfo
      : undefined;

  // 메시지 큐 메뉴
  const messageQueueMenuInfo: MenuInfo[] = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_MESSAGEQUEUE,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.MESSAGE_QUEUE'),
          value: BASE_MENU_VALUE.MESSAGE_QUEUE,
          children: getMenuList(getMessageQueueMenu())[0]?.children,
          dashboardList: messageQueueDashboardList,
          analysisBoardList: messageQueueAnalysisBoardList,
        },
      ]
    : [];

  // 클라우드 > AWS 메뉴
  const subMenuCategories: ('computing' | 'storage' | 'database' | 'management')[] = [
    'computing',
    'storage',
    'database',
    'management',
  ];

  const cloudAwsMenuInfo: MenuInfo[] = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_AWS,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.AWS'),
          value: `${BASE_MENU_VALUE.CLOUD}_aws`,
          subMenuList: subMenuCategories
            .map((category) => {
              return {
                text: t(`WORD.${category.toUpperCase()}`),
                value: '',
                children: getMenuList(getCloudNaviMenu('aws', category))[0]?.children,
              };
            })
            .filter(({ children }) => children && children?.length > 0),
        },
      ]
    : [];

  // 클라우드 > 대시보드 메뉴
  const cloudDashboardList = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_DASHBOARD,
  )?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;

  const cloudAnalysisBoardList = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD_DASHBOARD,
  )?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;

  // 클라우드 메뉴 목록
  const cloudMenuInfo: MenuInfo[] = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLOUD,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.CLOUD'),
          value: BASE_MENU_VALUE.CLOUD,
          children: [
            ...(getMenuList(getCloudNaviMenu())[0]?.children || []),
            ...(cloudAwsMenuInfo.length > 0
              ? [
                  {
                    text: t('WORD.CLOUD_SERVICE'),
                    value: '',
                    children: [...cloudAwsMenuInfo],
                  },
                ]
              : []),
          ],
          dashboardList: cloudDashboardList,
          analysisBoardList: cloudAnalysisBoardList,
        },
      ]
    : [];

  // 쿠버네티스 > 대시보드 메뉴
  const kubernetesDashboardList = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_DASHBOARD,
  )?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;

  const kubernetesAnalysisBoardList = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_DASHBOARD,
  )?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;

  // 쿠버네티스 GNB 그룹은 kubernetesGnbGroups.define(K8S_GNB_GROUPS)이 SSOT로 소유한다(DSP-44218).
  // route에서 만든 MenuInfo를 value로 찾아 그룹/라벨/순서/서브그룹을 define대로 조립한다.
  // (기존 findKubernetesMenuByPath 화이트리스트·relabel·getKubernetesNaviMenu(subMenu) 분기 제거)
  // 모든 K8s 리프 MenuInfo를 value로 색인 (getMenuList가 value/path/권한을 이미 계산).
  const kubernetesMenuByValue = new Map<string, MenuInfo>(
    getMenuList(getKubernetesNaviMenu())
      .flatMap((parent) => parent.children ?? [])
      .map((menu) => [menu.value, menu]),
  );

  const buildK8sGnbItems = (item: K8sGnbItem): MenuInfo[] => {
    // 통합 Workload 뷰 ?type= 탭 (value는 kubernetes_workload 하나 공유)
    if ('types' in item) {
      const base = kubernetesMenuByValue.get(item.value);
      return base
        ? item.types.map(({ type, label }) => ({
            text: translateEn(label),
            value: `${base.value}_${type}`,
            path: `${base.path}?type=${type}`,
            meta: base.meta,
          }))
        : [];
    }
    // 서브그룹(Batch/OpenShift) — 앞에 구분선(|). OpenShift는 클러스터 존재 시에만 노출.
    if ('subGroupLabel' in item) {
      if (item.requiresOpenShift && !openShiftCluster.value.exists) {
        return [];
      }
      const children = item.items.flatMap(buildK8sGnbItems);
      return children.length > 0
        ? [{ text: t(`WORD.GNB.${item.subGroupLabel}`), value: '', children }]
        : [];
    }
    // 리프 — route에서 만든 MenuInfo(value/path/권한)에 define 라벨 적용
    const menu = kubernetesMenuByValue.get(item.value);
    return menu ? [{ ...menu, text: translateEn(item.label) }] : [];
  };

  // define 순서대로 그룹 조립. 접근 가능한 leaf가 하나도 없으면 그룹 미노출.
  const kubernetesGroupsMenuInfo: MenuInfo[] = K8S_GNB_GROUPS.flatMap((group) => {
    const subMenuList = group.items.flatMap(buildK8sGnbItems);
    return subMenuList.length > 0
      ? [
          {
            text: t(`WORD.GNB.${group.groupLabel}`),
            value: `${BASE_MENU_VALUE.KUBERNETES}_${group.valueSuffix}`,
            showSubHeader: true, // K8s 모니터링 그룹은 sub-header(탭 바) 노출
            subMenuList,
          },
        ]
      : [];
  });

  const kubernetesPerformanceAnalysisMenuInfo = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES_ANALYSIS,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.PERFORMANCE_ANALYSIS'),
          value: `${BASE_MENU_VALUE.KUBERNETES}_performanceAnalysis`,
          // 성능분석은 기획상 sub-header 미노출 → showSubHeader를 설정하지 않는다.
          subMenuList: getMenuList(getKubernetesAnalysisMenu())[0]?.children || [],
        },
      ]
    : [];

  // 쿠버네티스 메뉴 목록
  const kubernetesMenuInfo: MenuInfo[] = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_KUBERNETES,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.KUBERNETES'),
          value: BASE_MENU_VALUE.KUBERNETES,
          children: [...kubernetesPerformanceAnalysisMenuInfo, ...kubernetesGroupsMenuInfo],
          dashboardList: kubernetesDashboardList,
          analysisBoardList: kubernetesAnalysisBoardList,
        },
      ]
    : [];

  // 어플리케이션 > 대시보드 메뉴
  const applicationDashboardList = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_DASHBOARD,
  )?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const applicationAnalysisBoardList = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_DASHBOARD,
  )?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const rumChildren = getMenuList(getRumNaviMenu())[0]?.children;
  // RUM의 경우 자식 권한을 개별로 갖기 때문에, getRumNaviMenu 내부에서 자식 권한 체크 후 메뉴리스트 추출

  // Application 성능 분석 메뉴 (LNB 섹션 타이틀 기반 그룹 노출)
  const applicationPerformanceAnalysisMenuInfo = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_ANALYSIS,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.PERFORMANCE_ANALYSIS'),
          value: `${BASE_MENU_VALUE.APPLICATION}_performanceAnalysis`,
          subMenuList: (() => {
            const applicationPerformanceAnalysisMenus =
              getMenuList(getApplicationPerformanceAnalysisMenu())[0]?.children || [];

            const filterMenusByPath = (menuPaths: string[]) =>
              applicationPerformanceAnalysisMenus.filter(({ path }) =>
                Boolean(path && menuPaths.some((menuPath) => path.endsWith(menuPath))),
              );

            return [
              {
                text: t('WORD.GNB.TRANSACTION'),
                value: `${BASE_MENU_VALUE.APPLICATION}_performanceAnalysis_transaction`,
                children: filterMenusByPath([
                  'performance-analysis/transaction-trend',
                  'performance-analysis/top-transaction-summary',
                ]),
              },
              {
                text: t('WORD.GNB.ERROR_TRACKING'),
                value: `${BASE_MENU_VALUE.APPLICATION}_performanceAnalysis_errorTracking`,
                children: filterMenusByPath(['performance-analysis/exception-summary']),
              },
              {
                text: t('WORD.GNB.PERFORMANCE_TREND'),
                value: `${BASE_MENU_VALUE.APPLICATION}_performanceAnalysis_performanceTrend`,
                children: filterMenusByPath([
                  'performance-analysis/daily-report',
                  'performance-analysis/trend-analysis',
                ]),
              },
              {
                text: t('WORD.GNB.SERVICE_IMPACT_ANALYSIS'),
                value: `${BASE_MENU_VALUE.APPLICATION}_performanceAnalysis_serviceImpactAnalysis`,
                children: filterMenusByPath([
                  'performance-analysis/performance-heatmap',
                  'performance-analysis/agent-workload',
                ]),
              },
              {
                text: t('WORD.GNB.SQL_ANALYSIS'),
                value: `${BASE_MENU_VALUE.APPLICATION}_performanceAnalysis_sqlAnalysis`,
                children: filterMenusByPath(['performance-analysis/top-sql-summary']),
              },
              {
                text: t('WORD.GNB.RESOURCE_JVM_ANALYSIS'),
                value: `${BASE_MENU_VALUE.APPLICATION}_performanceAnalysis_resourceJvmAnalysis`,
                children: filterMenusByPath([
                  'performance-analysis/thread-dump',
                  'performance-analysis/gc-log-analyzer',
                ]),
              },
            ].filter(({ children }) => children.length > 0);
          })(),
        },
      ]
    : [];

  // 어플리케이션 메뉴 목록
  const applicationMenuInfo = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.APPLICATION'),
          value: BASE_MENU_VALUE.APPLICATION,
          children: [
            ...applicationPerformanceAnalysisMenuInfo,
            ...(getMenuList(getApplicationNaviMenu())[0]?.children ?? []),
            ...(rumChildren
              ? [
                  {
                    text: t('WORD.GNB.RUM'),
                    value: `${BASE_MENU_VALUE.APPLICATION}_rum`,
                    children: rumChildren,
                  },
                ]
              : []),
          ],
          dashboardList: applicationDashboardList,
          analysisBoardList: applicationAnalysisBoardList,
        },
      ]
    : [];

  // 데이터베이스 메뉴 목록
  const databaseDashboardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subDashboardMenuInfo
      : undefined;
  const databaseAnalysisBoardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subAnalysisBoardMenuInfo
      : undefined;

  // Database DBMS Type별 메뉴 정보
  const databaseDbmsTypeMenuInfo = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ANALYSIS,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.DBMS_TYPE'),
          value: `${BASE_MENU_VALUE.DATABASE}_dbmsType`,
          children: [
            ...(findPermissionByTranslateId(
              ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ORACLE,
            )?.menuOnOff
              ? [
                  {
                    text: t('WORD.GNB.ORACLE'),
                    value: BASE_MENU_VALUE.ORACLE,
                    subMenuList: (() => {
                      const featuredDashboardMenu = {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.ORACLE}_featuredDashboard`,
                        children: [
                          {
                            text: t('WORD.GNB.MULTI_VIEW'),
                            value: `${BASE_MENU_VALUE.ORACLE}_multiView`,
                            path: '/oracle/multiView',
                          },
                        ],
                      };
                      const hasDashboardPermission = hasDatabaseDashboardPermission(DB_TYPE.ORACLE);
                      return [
                        ...(hasDashboardPermission ? [featuredDashboardMenu] : []),
                        ...getMenuList(getOracleNaviMenu()),
                      ];
                    })(),
                  },
                ]
              : []),
            ...(findPermissionByTranslateId(
              ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_POSTGRESQL,
            )?.menuOnOff
              ? [
                  {
                    text: t('WORD.GNB.POSTGRESQL'),
                    value: BASE_MENU_VALUE.POSTGRESQL,
                    subMenuList: (() => {
                      const featuredDashboardMenu = {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.POSTGRESQL}_featuredDashboard`,
                        children: [
                          {
                            text: t('WORD.GNB.MULTI_VIEW'),
                            value: `${BASE_MENU_VALUE.POSTGRESQL}_multiView`,
                            path: '/postgresql/multiView',
                          },
                          {
                            text: t('WORD.GNB.SINGLE_VIEW'),
                            value: `${BASE_MENU_VALUE.POSTGRESQL}_singleView`,
                            path: '/postgresql/singleView',
                          },
                        ],
                      };
                      const hasDashboardPermission = hasDatabaseDashboardPermission(
                        DB_TYPE.POSTGRESQL,
                      );
                      return [
                        ...(hasDashboardPermission ? [featuredDashboardMenu] : []),
                        ...getMenuList(getPgNaviMenu()),
                      ];
                    })(),
                  },
                ]
              : []),
            ...(findPermissionByTranslateId(
              ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_MYSQL,
            )?.menuOnOff
              ? [
                  {
                    text: t('WORD.GNB.MYSQL'),
                    value: BASE_MENU_VALUE.MYSQL,
                    subMenuList: (() => {
                      const featuredDashboardMenu = {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.MYSQL}_featuredDashboard`,
                        children: [
                          {
                            text: t('WORD.GNB.MULTI_VIEW'),
                            value: `${BASE_MENU_VALUE.MYSQL}_multiView`,
                            path: '/mysql/multiView',
                          },
                          {
                            text: t('WORD.GNB.SINGLE_VIEW'),
                            value: `${BASE_MENU_VALUE.MYSQL}_singleView`,
                            path: '/mysql/singleView',
                          },
                        ],
                      };
                      const hasDashboardPermission = hasDatabaseDashboardPermission(DB_TYPE.MYSQL);
                      return [
                        ...(hasDashboardPermission ? [featuredDashboardMenu] : []),
                        ...getMenuList(getMysqlNaviMenu()),
                      ];
                    })(),
                  },
                ]
              : []),
            ...(findPermissionByTranslateId(
              ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_SQLSERVER,
            )?.menuOnOff
              ? [
                  {
                    text: t('WORD.GNB.SQL_SERVER'),
                    value: BASE_MENU_VALUE.SQLSERVER,
                    subMenuList: (() => {
                      const featuredDashboardMenu = {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.SQLSERVER}_featuredDashboard`,
                        children: [
                          {
                            text: t('WORD.GNB.SINGLE_VIEW'),
                            value: `${BASE_MENU_VALUE.SQLSERVER}_singleView`,
                            path: '/sqlserver/singleView',
                          },
                        ],
                      };
                      const hasDashboardPermission = hasDatabaseDashboardPermission(
                        DB_TYPE.SQLSERVER,
                      );
                      return [
                        ...(hasDashboardPermission ? [featuredDashboardMenu] : []),
                        ...getMenuList(getSqlServerNaviMenu()),
                      ];
                    })(),
                  },
                ]
              : []),
            ...(findPermissionByTranslateId(
              ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ALTIBASE,
            )?.menuOnOff
              ? [
                  {
                    text: t('WORD.GNB.ALTIBASE'),
                    value: BASE_MENU_VALUE.ALTIBASE,
                    subMenuList: (() => {
                      const featuredDashboardMenu = {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.ALTIBASE}_featuredDashboard`,
                        children: [
                          {
                            text: t('WORD.GNB.MULTI_VIEW'),
                            value: `${BASE_MENU_VALUE.ALTIBASE}_multiView`,
                            path: '/altibase/multiView',
                          },
                        ],
                      };
                      const hasDashboardPermission = hasDatabaseDashboardPermission(
                        DB_TYPE.ALTIBASE,
                      );
                      return [
                        ...(hasDashboardPermission ? [featuredDashboardMenu] : []),
                        ...getMenuList(getAltibaseNaviMenu()),
                      ];
                    })(),
                  },
                ]
              : []),
            ...(findPermissionByTranslateId(
              ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_TIBERO,
            )?.menuOnOff
              ? [
                  {
                    text: t('WORD.GNB.TIBERO'),
                    value: BASE_MENU_VALUE.TIBERO,
                    subMenuList: getMenuList(getTiberoNaviMenu()),
                  },
                ]
              : []),
            ...(findPermissionByTranslateId(
              ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_CLICKHOUSE,
            )?.menuOnOff
              ? [
                  {
                    text: t('WORD.GNB.CLICKHOUSE'),
                    value: BASE_MENU_VALUE.CLICKHOUSE,
                    subMenuList: getMenuList(getClickhouseNaviMenu()),
                  },
                ]
              : []),
            ...(findPermissionByTranslateId(
              ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_REDSHIFT,
            )?.menuOnOff
              ? [
                  {
                    text: t('WORD.GNB.REDSHIFT'),
                    value: BASE_MENU_VALUE.REDSHIFT,
                    subMenuList: getMenuList(getRedshiftNaviMenu()),
                  },
                ]
              : []),
            ...(findPermissionByTranslateId(
              ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_SINGLESTORE,
            )?.menuOnOff
              ? [
                  {
                    text: t('WORD.GNB.SINGLESTORE'),
                    value: BASE_MENU_VALUE.SINGLESTORE,
                    subMenuList: (() => {
                      const featuredDashboardMenu = {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.SINGLESTORE}_featuredDashboard`,
                        children: [
                          {
                            text: t('WORD.GNB.CLUSTER_VIEW'),
                            value: `${BASE_MENU_VALUE.SINGLESTORE}_clusterView`,
                            path: '/singlestore/clusterView',
                          },
                        ],
                      };
                      const hasDashboardPermission = hasDatabaseDashboardPermission(
                        DB_TYPE.SINGLESTORE,
                      );
                      return [
                        ...(hasDashboardPermission ? [featuredDashboardMenu] : []),
                        ...getMenuList(getSingleStoreNaviMenu()),
                      ];
                    })(),
                  },
                ]
              : []),
          ],
        },
      ].filter((menu) => menu.children.length > 0)
    : [];

  const databaseMenuInfo = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE,
  )?.menuOnOff
    ? [
        {
          text: viewMode !== VIEW_MODE.MAXGAUGE ? t('WORD.GNB.DATABASE') : t('WORD.GNB.OVERVIEW'),
          value: BASE_MENU_VALUE.DATABASE,
          children: [
            ...(getMenuList(getDatabaseNaviMenu())[0]?.children || []),
            // maxgauge 모드에서는 databaseDbmsTypeMenuInfo 제외 (각 DB 타입이 이미 1depth 메뉴로 분산되어 있음)
            ...(viewMode !== VIEW_MODE.MAXGAUGE ? databaseDbmsTypeMenuInfo : []),
          ],
          dashboardList: databaseDashboardList,
          analysisBoardList: databaseAnalysisBoardList,
        },
      ]
    : [];

  // 서비스 > 대시보드 메뉴
  const serviceDashboardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_BUSINESS_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subDashboardMenuInfo
      : undefined;
  const serviceAnalysisBoardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_BUSINESS_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subAnalysisBoardMenuInfo
      : undefined;
  // 서비스 메뉴 목록
  const serviceMenuInfo = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_BUSINESS,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.BUSINESS'),
          value: BASE_MENU_VALUE.BUSINESS,
          children: getMenuList(getBusinessNaviMenu())[0]?.children,
          dashboardList: serviceDashboardList,
          analysisBoardList: serviceAnalysisBoardList,
        },
      ]
    : [];

  // 로그 > 대시보드 메뉴
  const logDashboardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_LOGS_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subDashboardMenuInfo
      : undefined;
  const logAnalysisBoardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_LOGS_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subAnalysisBoardMenuInfo
      : undefined;

  // 로그 메뉴 목록
  const loggingSubMenuInfo = {
    text: t('WORD.GNB.LOGGING'),
    value: `${BASE_MENU_VALUE.LOGS}_logging`,
    children: getMenuList(getLoggingNaviMenu())[0]?.children,
  };
  const streamSubMenuInfo = {
    text: t('WORD.GNB.STREAM'),
    value: `${BASE_MENU_VALUE.LOGS}_stream`,
    children: getMenuList(getStreamNaviMenu())[0]?.children,
  };
  // children이 있는 경우만 추가
  const logSubMenus = [loggingSubMenuInfo, streamSubMenuInfo].filter(
    (menu) => Array.isArray(menu.children) && menu.children.length > 0,
  );
  const logMenuInfo = findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_LOGS)
    ?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.LOGS'),
          value: BASE_MENU_VALUE.LOGS,
          children: logSubMenus,
          dashboardList: logDashboardList,
          analysisBoardList: logAnalysisBoardList,
        },
      ]
    : [];

  // 알림 > 대시보드 메뉴
  const alertDashboardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subDashboardMenuInfo
      : undefined;
  const alertAnalysisBoardList =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT_DASHBOARD)
      ?.menuOnOff && viewMode !== VIEW_MODE.MAXGAUGE
      ? subAnalysisBoardMenuInfo
      : undefined;
  // 알림 메뉴 목록
  const alertMenuInfo = findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALERT)
    ?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.ALERT'),
          value: BASE_MENU_VALUE.ALERT,
          children: getMenuList(getAlertNaviMenu(roleId))[0]?.children,
          dashboardList: alertDashboardList,
          analysisBoardList: alertAnalysisBoardList,
        },
      ]
    : [];

  // 도구 1-Depth 메뉴 (신설)
  // 도구 메뉴에서 스크립트 매니저 + 쿼리 매니저 모두 가져오기
  const toolMenuItems = getMenuList(getToolMenu())[0]?.children || [];

  // 도구 1-Depth 메뉴 생성 (스크립트 매니저 + 쿼리 매니저 통합)
  const toolMenuInfo = findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_TOOL)
    ?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.TOOL'),
          value: BASE_MENU_VALUE.TOOL,
          children: toolMenuItems,
        },
      ]
    : [];

  // 리포트 메뉴 목록
  const reportMenuInfo = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_REPORT,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.REPORT'),
          value: BASE_MENU_VALUE.REPORT,
          children: getMenuList(getReportNaviMenu())[0]?.children,
        },
      ]
    : [];

  // 사전 정의 리포트 메뉴 목록
  const predefinedReportMenuInfo = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_PREDEFINED_REPORT,
  )?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.PREDEFINED_REPORT'),
          value: BASE_MENU_VALUE.PREDEFINED_REPORT,
          children: getMenuList(getPredefinedReportNaviMenu())[0]?.children,
        },
      ]
    : [];

  // AIOps 메뉴 목록 (1-Depth 클릭 시 AIOps 대시보드 화면으로 바로 이동)
  const aiopsMenuInfo = findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_AIOPS)
    ?.menuOnOff
    ? [
        {
          text: t('WORD.GNB.AIOPS'),
          value: BASE_MENU_VALUE.AIOPS,
          path: '/aiops/dashboard',
          children: undefined,
        },
      ]
    : [];

  // 고객지원 메뉴 목록
  const helpMenuInfo = {
    text: t('WORD.GNB.HELP.TITLE'),
    value: BASE_MENU_VALUE.HELP,
    subMenuList: getMenuList(getHelpNaviMenu()),
  };
  const hasHelpMenuPermission =
    findPermissionByTranslateId(ROLE_PERMISSION_KEY.MONITORING.MONITORING_USER_MANUAL)?.menuOnOff ??
    false;

  // 설정 메뉴 목록
  const myInfoMenuInfo = {
    text: t('WORD.GNB.SETTING'),
    value: BASE_MENU_VALUE.SETTINGS,
    subMenuList: getMenuList(getConfigNaviMenu(roleId)),
  };

  const hasFeaturedDashboardPermission = !!findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.DASHBOARD.DASHBOARD_FEATURED_DASHBOARD,
  )?.listOnOff;

  // maxgauge oracle
  const maxgaugeOracleDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ORACLE_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeOracleAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ORACLE_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeOracleMenuInfo =
    instanceCount.value?.oracle &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ORACLE,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.ORACLE'),
            value: BASE_MENU_VALUE.ORACLE,
            dashboardList: maxgaugeOracleDashboardList,
            analysisBoardList: maxgaugeOracleAnalysisBoardList,
            children: [
              ...(() => {
                const instanceViewChildren =
                  getMenuList(getOracleNaviMenu('instanceView'))[0]?.children ?? [];
                return hasFeaturedDashboardPermission && instanceViewChildren.length
                  ? [
                      {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.ORACLE}_featuredDashboard`,
                        children: instanceViewChildren,
                      },
                    ]
                  : [];
              })(),
              ...createMaxgaugeMenuItem(DB_TYPE.ORACLE),
            ].filter((item) => !isNil(item)),
          },
        ]
      : [];

  // maxgauge pg
  const maxgaugePgDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_POSTGRESQL_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugePgAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_POSTGRESQL_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugePgMenuInfo =
    instanceCount.value?.postgresql &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_POSTGRESQL,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.POSTGRESQL'),
            value: BASE_MENU_VALUE.POSTGRESQL,
            dashboardList: maxgaugePgDashboardList,
            analysisBoardList: maxgaugePgAnalysisBoardList,
            children: [
              ...(() => {
                const instanceViewChildren =
                  getMenuList(getPgNaviMenu('instanceView'))[0]?.children ?? [];
                return hasFeaturedDashboardPermission && instanceViewChildren.length
                  ? [
                      {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.POSTGRESQL}_featuredDashboard`,
                        children: instanceViewChildren,
                      },
                    ]
                  : [];
              })(),
              ...createMaxgaugeMenuItem(DB_TYPE.POSTGRESQL),
            ].filter((item) => !isNil(item)),
          },
        ]
      : [];

  // maxgauge mysql
  const maxgaugeMysqlDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MYSQL_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeMysqlAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MYSQL_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeMysqlMenuInfo =
    instanceCount.value?.mysql &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MYSQL,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.MYSQL'),
            value: BASE_MENU_VALUE.MYSQL,
            dashboardList: maxgaugeMysqlDashboardList,
            analysisBoardList: maxgaugeMysqlAnalysisBoardList,
            children: [
              ...(() => {
                const instanceViewChildren =
                  getMenuList(getMysqlNaviMenu('instanceView'))[0]?.children ?? [];
                return hasFeaturedDashboardPermission && instanceViewChildren.length
                  ? [
                      {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.MYSQL}_featuredDashboard`,
                        children: instanceViewChildren,
                      },
                    ]
                  : [];
              })(),
              ...createMaxgaugeMenuItem(DB_TYPE.MYSQL),
            ].filter((item) => !isNil(item)),
          },
        ]
      : [];

  // maxgauge SQL Server
  const maxgaugeSqlServerDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_SQLSERVER_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeSqlServerAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_SQLSERVER_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeSqlServerMenuInfo =
    instanceCount.value?.sqlserver &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_SQLSERVER,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.SQL_SERVER'),
            value: BASE_MENU_VALUE.SQLSERVER,
            dashboardList: maxgaugeSqlServerDashboardList,
            analysisBoardList: maxgaugeSqlServerAnalysisBoardList,
            children: [
              ...(() => {
                const instanceViewChildren =
                  getMenuList(getSqlServerNaviMenu('instanceView'))[0]?.children ?? [];
                return hasFeaturedDashboardPermission && instanceViewChildren.length
                  ? [
                      {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.SQLSERVER}_featuredDashboard`,
                        children: instanceViewChildren,
                      },
                    ]
                  : [];
              })(),
              ...createMaxgaugeMenuItem(DB_TYPE.SQLSERVER),
            ].filter((item) => !isNil(item)),
          },
        ]
      : [];

  // maxgauge Redis
  const maxgaugeRedisDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_REDIS_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeRedisAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_REDIS_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeRedisMenuInfo =
    instanceCount.value?.redis &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_REDIS,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.REDIS'),
            value: BASE_MENU_VALUE.REDIS,
            dashboardList: maxgaugeRedisDashboardList,
            analysisBoardList: maxgaugeRedisAnalysisBoardList,
          },
        ]
      : [];

  // maxgauge Cubrid
  const maxgaugeCubridDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CUBRID_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeCubridAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CUBRID_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;

  const maxgaugeCubridMenuInfo =
    instanceCount.value?.cubrid &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CUBRID,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.CUBRID'),
            value: BASE_MENU_VALUE.CUBRID,
            dashboardList: maxgaugeCubridDashboardList,
            analysisBoardList: maxgaugeCubridAnalysisBoardList,
          },
        ]
      : [];

  // maxgauge Mongo
  const maxgaugeMongoDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MONGO_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeMongoAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MONGO_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeMongoMenuInfo =
    instanceCount.value?.mongodb &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_MONGO,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.MONGODB'),
            value: BASE_MENU_VALUE.MONGODB,
            dashboardList: maxgaugeMongoDashboardList,
            analysisBoardList: maxgaugeMongoAnalysisBoardList,
          },
        ]
      : [];

  // maxgauge Tibero
  const maxgaugeTiberoDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_TIBERO_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeTiberoAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_TIBERO_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeTiberoMenuInfo =
    instanceCount.value?.tibero &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_TIBERO,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.TIBERO'),
            value: BASE_MENU_VALUE.TIBERO,
            dashboardList: maxgaugeTiberoDashboardList,
            analysisBoardList: maxgaugeTiberoAnalysisBoardList,
            children: [...createMaxgaugeMenuItem(DB_TYPE.TIBERO)].filter((item) => !isNil(item)),
          },
        ]
      : [];

  // maxgauge Altibase
  const maxgaugeAltibaseDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALTIBASE_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeAltibaseAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALTIBASE_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeAltibaseMenuInfo =
    instanceCount.value?.altibase &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_ALTIBASE,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.ALTIBASE'),
            value: BASE_MENU_VALUE.ALTIBASE,
            dashboardList: maxgaugeAltibaseDashboardList,
            analysisBoardList: maxgaugeAltibaseAnalysisBoardList,
            children: [
              ...(() => {
                const instanceViewChildren =
                  getMenuList(getAltibaseNaviMenu('instanceView'))[0]?.children ?? [];
                return hasFeaturedDashboardPermission && instanceViewChildren.length
                  ? [
                      {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.ALTIBASE}_featuredDashboard`,
                        children: instanceViewChildren,
                      },
                    ]
                  : [];
              })(),
              ...createMaxgaugeMenuItem(DB_TYPE.ALTIBASE),
            ].filter((item) => !isNil(item)),
          },
        ]
      : [];

  // maxgauge Clickhouse
  const maxgaugeClickhouseDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLICKHOUSE_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeClickhouseAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLICKHOUSE_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeClickhouseMenuInfo =
    instanceCount.value?.clickhouse &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_CLICKHOUSE,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.CLICKHOUSE'),
            value: BASE_MENU_VALUE.CLICKHOUSE,
            dashboardList: maxgaugeClickhouseDashboardList,
            analysisBoardList: maxgaugeClickhouseAnalysisBoardList,
            children: [...createMaxgaugeMenuItem(DB_TYPE.CLICKHOUSE)].filter(
              (item) => !isNil(item),
            ),
          },
        ]
      : [];

  // maxgauge Redshift
  const maxgaugeRedshiftDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_REDSHIFT_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeRedshiftAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_REDSHIFT_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeRedshiftMenuInfo =
    instanceCount.value?.redshift &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_REDSHIFT,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.REDSHIFT'),
            value: BASE_MENU_VALUE.REDSHIFT,
            dashboardList: maxgaugeRedshiftDashboardList,
            analysisBoardList: maxgaugeRedshiftAnalysisBoardList,
            children: [...createMaxgaugeMenuItem(DB_TYPE.REDSHIFT)].filter((item) => !isNil(item)),
          },
        ]
      : [];

  // maxgauge Singlestore
  const maxgaugeSinglestoreDashboardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_SINGLESTORE_DASHBOARD,
  })?.menuOnOff
    ? subDashboardMenuInfo
    : undefined;
  const maxgaugeSinglestoreAnalysisBoardList = findPermissionByTranslateId({
    [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_SINGLESTORE_DASHBOARD,
  })?.menuOnOff
    ? subAnalysisBoardMenuInfo
    : undefined;
  const maxgaugeSinglestoreMenuInfo =
    instanceCount.value?.singlestore &&
    findPermissionByTranslateId({
      [VIEW_MODE.MAXGAUGE]: ROLE_PERMISSION_KEY.MONITORING.MONITORING_SINGLESTORE,
    })?.menuOnOff
      ? [
          {
            text: t('WORD.GNB.SINGLESTORE'),
            value: BASE_MENU_VALUE.SINGLESTORE,
            dashboardList: maxgaugeSinglestoreDashboardList,
            analysisBoardList: maxgaugeSinglestoreAnalysisBoardList,
            children: [
              ...(() => {
                const instanceViewChildren =
                  getMenuList(getSingleStoreNaviMenu('instanceView'))[0]?.children ?? [];
                return hasFeaturedDashboardPermission && instanceViewChildren.length
                  ? [
                      {
                        text: t('WORD.GNB.PRESET_DASHBOARD'),
                        value: `${BASE_MENU_VALUE.SINGLESTORE}_featuredDashboard`,
                        children: instanceViewChildren,
                      },
                    ]
                  : [];
              })(),
              ...createMaxgaugeMenuItem(DB_TYPE.SINGLESTORE),
            ].filter((item) => !isNil(item)),
          },
        ]
      : [];

  // DSP-21751: 맥스게이지는 각 DB 를 1-depth GNB 로 flatten 하는데, 최상단 항목은 상위 권한
  // (MONITORING_ORACLE 등)만으로 노출돼 하위 권한을 전부 off 해도 빈 메뉴가 클릭 요소로 남았다.
  // (엑셈원은 extractAccessibleRoutes 가 빈 부모를 제거하지만 이 flatten 경로엔 닿지 않음.)
  // 접근 가능한 콘텐츠(children/dashboard/analysis/subMenu)가 하나도 없으면 최상단을 숨긴다.
  const hasAccessibleMaxgaugeDbMenu = (menu: MenuInfo): boolean =>
    !!menu.children?.length ||
    !!menu.dashboardList ||
    !!menu.analysisBoardList ||
    !!menu.subMenuList?.length;

  // maxgauge PA > DB 메뉴 목록
  const maxgaugePaDbMenuInfo = findPermissionByTranslateId(
    ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_ANALYSIS,
  )?.menuOnOff
    ? [
        ...maxgaugeOracleMenuInfo,
        ...maxgaugePgMenuInfo,
        ...maxgaugeMysqlMenuInfo,
        ...maxgaugeSqlServerMenuInfo,
        ...maxgaugeRedisMenuInfo,
        ...maxgaugeCubridMenuInfo,
        ...maxgaugeMongoMenuInfo,
        ...maxgaugeTiberoMenuInfo,
        ...maxgaugeAltibaseMenuInfo,
        ...maxgaugeClickhouseMenuInfo,
        ...maxgaugeRedshiftMenuInfo,
        ...maxgaugeSinglestoreMenuInfo,
      ].filter(hasAccessibleMaxgaugeDbMenu)
    : [];

  // 플랫폼별 타겟 카운트 조회 (EXEMONE 모드에서만 활성화)
  // 타겟 0개인 미사용 플랫폼 → GNB 1Depth 미노출 (DSP-42852)
  const { data: categoryCountData, isFetched: isCategoryCountFetched } =
    useGetV11TargetByCategoryCount({
      query: {
        select: (response) => response.data.data?.[0] ?? null,
        enabled: viewMode === VIEW_MODE.EXEMONE,
      },
    });

  // 카운트 응답 전(EXEMONE 한정)에는 GNB 전체를 숨겨, 미사용 플랫폼이 떴다 사라지는 깜빡임을 방지
  const isMenuReady = computed(
    () => viewMode !== VIEW_MODE.EXEMONE || isCategoryCountFetched.value,
  );

  // BASE_MENU_VALUE → API 카테고리 키 매핑 (message-queue → messageQueue)
  const MENU_API_CATEGORY: Partial<Record<string, string>> = {
    [BASE_MENU_VALUE.INFRASTRUCTURE]: 'infrastructure',
    [BASE_MENU_VALUE.MESSAGE_QUEUE]: 'messageQueue',
    [BASE_MENU_VALUE.CLOUD]: 'cloud',
    [BASE_MENU_VALUE.KUBERNETES]: 'kubernetes',
    [BASE_MENU_VALUE.APPLICATION]: 'application',
    [BASE_MENU_VALUE.DATABASE]: 'database',
    [BASE_MENU_VALUE.GPU]: 'gpu',
  };

  const hasCategoryTargets = (menuValue: string): boolean => {
    const apiKey = MENU_API_CATEGORY[menuValue];
    if (!apiKey) return true;
    const data = categoryCountData.value;
    if (data == null) return true; // 데이터 로드 전 → 기본 full 메뉴 표시 (optimistic)
    const categoryObj = (data as Record<string, unknown>)[apiKey];
    if (!categoryObj || typeof categoryObj !== 'object') return false;
    return (
      Object.values(categoryObj as Record<string, unknown>).reduce<number>(
        (acc, v) => acc + (typeof v === 'number' ? v : 0),
        0,
      ) > 0
    );
  };

  // 타겟 0개인 미사용 플랫폼 → GNB 1Depth 미노출 (DSP-42852)
  const isPlatformVisible = (menu: MenuInfo): boolean => {
    if (menu.path) return true;
    return hasCategoryTargets(menu.value);
  };

  const exemONEMenuInfo = computed<MenuInfo[]>(() => [
    homeMenuInfo,
    ...aiopsMenuInfo,
    dashboardMenuInfo,
    ...infrastructureMenuInfo.filter(isPlatformVisible),
    ...messageQueueMenuInfo.filter(isPlatformVisible),
    ...cloudMenuInfo.filter(isPlatformVisible),
    ...kubernetesMenuInfo.filter(isPlatformVisible),
    ...gpuMenuInfo.filter(isPlatformVisible),
    ...applicationMenuInfo.filter(isPlatformVisible),
    ...databaseMenuInfo.filter(isPlatformVisible),
    ...serviceMenuInfo,
    ...logMenuInfo,
    ...alertMenuInfo,
    ...toolMenuInfo,
    ...reportMenuInfo,
    ...predefinedReportMenuInfo,
    helpMenuInfo,
    myInfoMenuInfo,
  ]);

  const maxgaugeMenuInfo: MenuInfo[] = [
    homeMenuInfo,
    ...aiopsMenuInfo,
    dashboardMenuInfo,
    ...databaseMenuInfo,
    ...maxgaugePaDbMenuInfo,
    ...toolMenuInfo,
    ...infrastructureMenuInfo,
    ...cloudMenuInfo,
    ...serviceMenuInfo,
    ...logMenuInfo,
    ...alertMenuInfo,
    ...reportMenuInfo,
    ...predefinedReportMenuInfo,
    helpMenuInfo,
    myInfoMenuInfo,
  ];

  const intermaxMenuInfo: MenuInfo[] = [
    homeMenuInfo,
    ...aiopsMenuInfo,
    dashboardMenuInfo,
    ...infrastructureMenuInfo,
    ...messageQueueMenuInfo,
    ...applicationMenuInfo,
    ...serviceMenuInfo,
    ...logMenuInfo,
    ...alertMenuInfo,
    ...toolMenuInfo,
    ...reportMenuInfo,
    ...predefinedReportMenuInfo,
    helpMenuInfo,
    myInfoMenuInfo,
  ];

  const menuInfo = computed<MenuInfo[]>(() => {
    switch (viewMode) {
      case VIEW_MODE.EXEMONE:
        return exemONEMenuInfo.value;
      case VIEW_MODE.MAXGAUGE:
        return maxgaugeMenuInfo;
      case VIEW_MODE.INTERMAX:
        return intermaxMenuInfo;
      default:
        return exemONEMenuInfo.value;
    }
  });

  return {
    findMenuItem,
    getMenuList,
    menuInfo,
    isMenuReady,
    helpMenuInfo,
    myInfoMenuInfo,
    hasHelpMenuPermission,
  };
};

export const useDashboardFavorite = (dashboardInfo: MenuInfo) => {
  const platformDashboardStore = usePlatformDashboardStore();
  const { menuByPlatform, menuByAnalysisPlatform } = storeToRefs(platformDashboardStore);

  const getQueryParam = (search: string, path: string) => {
    const params = new URL(`http://${path}`).searchParams.get(search);
    return params === 'kubernetes' ? 'k8s' : params;
  };

  const getDashboardId = (path: string) => {
    const url = new URL(`http://${path}`);
    const pathSegments = url.pathname.split('/');
    return pathSegments[pathSegments.length - 1];
  };

  const isFavorited = computed(() => {
    const { path } = dashboardInfo;
    if (!path) return false;

    const platform = getQueryParam('rootMenu', path);
    const dashboardId = getDashboardId(path);
    if (!platform || !dashboardId || !menuByPlatform.value[platform]) {
      return false;
    }
    return !!menuByPlatform.value[platform][dashboardId]?.favoriteId;
  });

  const isAnalysisFavorited = computed(() => {
    const { path } = dashboardInfo;
    if (!path) return false;

    const platform = getQueryParam('rootMenu', path);
    const dashboardId = getDashboardId(path);
    if (!platform || !dashboardId || !menuByAnalysisPlatform.value[platform]) {
      return false;
    }
    return !!menuByAnalysisPlatform.value[platform][dashboardId]?.favoriteId;
  });

  const updateFavorite = async () => {
    try {
      const { path } = dashboardInfo;
      if (!path) return;

      const dashboardId = Number(getDashboardId(path));
      if (!dashboardId) return;

      const platform = getQueryParam('rootMenu', path);
      if (!platform) return;

      const favoriteId =
        menuByPlatform.value[platform]?.[dashboardId]?.favoriteId ??
        menuByAnalysisPlatform.value[platform]?.[dashboardId]?.favoriteId;

      await postV6DashboardsFavorite(
        {
          dashboards: [
            {
              dashboardId,
              favoriteId,
              deleted: !!favoriteId,
            },
          ],
        },
        {},
        { frameName: FRAME_NAMES.DASHBOARD.FAVORITE_DASHBOARD_UPDATE },
      );

      invalidatePlatformDashboardMenuPath();
      useDashboardListStore().fetchDashboardList();
    } catch (err) {
      console.error(err);
    }
  };

  return {
    isFavorited,
    isAnalysisFavorited,
    updateFavorite,
  };
};
